import React from 'react';
import { useEffect } from 'react';
import { Formik, Form } from 'formik';
import validationSchema from './validationSchema';
import { DietIdEnum, DietModeType, FormPrompt } from '@front-activerse/common-components';
import { Modal, ModalHeader, ModalBody, Button, Row, Col } from 'reactstrap';
import createFormikInitialValues from './helpers/createFormikInitialValues';
import { withMobileAppMode, withLoading } from '@front-activerse/common-components';
import UniversalPlaceholder from '../Layout/UniversalPlaceholder';
import { dietSettings, stepConfigItem } from './types';

import SlideRoutes from 'react-slide-routes';
import StepProvider from './StepContext/StepProvider';
import StepWrapper from './components/StepWrapper';
import { Route, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Layout from '../Layout/Layout';
import DietSettingsLoaderPage from './DietSettingsLoaderPage';
import AcceptancePage from './AcceptancePage';
import { useTranslator, useAppContext, IRoute } from '@front-activerse/common-components';
import { UserFlagIdentifierType } from '@front-activerse/common-components';

interface IDietSettingsContainer {
  descendantRoutes: IRoute[];
  dietSettingsData: any;
  mobileAppMode: boolean;
  generateDiet: (values: dietSettings) => void;
  saveUserData: (values: dietSettings) => void;
  validateDietSettings: (values: dietSettings) => void;
  stepsSchema: stepConfigItem[];
  currentMainStep: string;
  dietTypes: {
    id: number;
    value: DietIdEnum;
    optionTitle: string;
    optionDescription: string;
    macro?: {
      protein: number;
      fat: number;
      carbs: number;
    };
  }[];
  dietLevels: {
    id: number;
    value: DietIdEnum;
    optionTitle: string;
    optionDescription: string;
  }[];
  dietModes?: {
    value: DietModeType;
    optionTitle: string;
    optionDescription: string;
  }[];
  isConsent?: boolean;
  setUserFlag: (identifier: UserFlagIdentifierType) => Promise<any>;
}

const DietSettingsContainer: React.FC<IDietSettingsContainer> = ({
  descendantRoutes,
  dietSettingsData,
  mobileAppMode,
  generateDiet,
  saveUserData,
  validateDietSettings,
  stepsSchema,
  currentMainStep,
  dietTypes,
  dietLevels,
  dietModes,
  isConsent,
  setUserFlag,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslator();
  const {
    consts: { SLIDE_ROUTES_DURATION, FIRST_DIET_SETTINGS_STEP_SLUG, APP_BRAND },
  } = useAppContext();

  useEffect(() => {
    // redirect to all diet setting when user not pass onboarding and try to access goal settings
    if (!dietSettingsData.me.birthDate && currentMainStep === 'goalSettings') {
      navigate(`/diet-settings/all/${FIRST_DIET_SETTINGS_STEP_SLUG}`);
    }
  }, [currentMainStep]);

  const initialValues = createFormikInitialValues(
    dietSettingsData,
    dietTypes,
    dietLevels,
    APP_BRAND as string
  );

  return (
    <Layout scrollY={false}>
      <div className="page-template-goal-settings">
        <Formik
          initialValues={initialValues}
          validationSchema={() => validationSchema(t)}
          validateOnChange
          onSubmit={(values) => console.log(values)}
        >
          {({ dirty }) => (
            <Form>
              {!mobileAppMode && (
                <FormPrompt hasUnsavedChanges={dirty} keyword="diet-settings">
                  {({
                    showModal,
                    toggleModal,
                    handleClick,
                  }: {
                    showModal: boolean;
                    toggleModal: () => void;
                    handleClick: () => void;
                  }) => (
                    <Modal isOpen={showModal} toggle={toggleModal} centered size="md">
                      <ModalHeader toggle={toggleModal} />
                      <ModalBody className="pt-0">
                        <h2 className="title-medium semi-bold text-center">
                          {t('warning.unsavedData.title')}
                        </h2>
                        <p className="text-center">{t('warning.unsavedData.content')}</p>
                        <Row>
                          <Col>
                            <Button
                              color="secondary"
                              className="w-100"
                              outline
                              onClick={toggleModal}
                            >
                              {t('warning.unsavedData.cancelButton')}
                            </Button>
                          </Col>
                          <Col>
                            <Button color="primary" className="w-100" onClick={handleClick}>
                              {t('warning.unsavedData.leaveButton')}
                            </Button>
                          </Col>
                        </Row>
                      </ModalBody>
                    </Modal>
                  )}
                </FormPrompt>
              )}

              <SlideRoutes duration={+SLIDE_ROUTES_DURATION}>
                <Route
                  key="diet-settings"
                  path="*"
                  element={
                    <StepProvider
                      currentMainStep={currentMainStep}
                      schema={stepsSchema}
                      generateDiet={generateDiet}
                      saveUserData={saveUserData}
                      validateDietSettings={validateDietSettings}
                      dietTypes={dietTypes}
                      dietLevels={dietLevels}
                      dietModes={dietModes}
                      isConsent={isConsent || false}
                    >
                      <div className="full-height2">
                        <Header title={t(`${currentMainStep}.progressBarTitle`)} />
                        <SlideRoutes duration={+SLIDE_ROUTES_DURATION}>
                          {descendantRoutes.map((route) => (
                            <Route
                              key={route.path}
                              path={route.path}
                              element={<StepWrapper>{route.element}</StepWrapper>}
                            />
                          ))}
                        </SlideRoutes>
                      </div>
                      <Footer />
                    </StepProvider>
                  }
                />
                <Route
                  key="acceptance"
                  path="/acceptance"
                  element={
                    <AcceptancePage
                      generateDiet={generateDiet}
                      setUserFlag={setUserFlag}
                      dietTypes={dietTypes}
                      dietLevels={dietLevels}
                    />
                  }
                />
                <Route key="loader" path="/loader" element={<DietSettingsLoaderPage />} />
              </SlideRoutes>
            </Form>
          )}
        </Formik>
      </div>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder fullHeightMinusHeader />;

export default withLoading(withMobileAppMode(DietSettingsContainer), Placeholder);
